import * as React from 'react';

import {
  DescriptionPage_SelfServeConfiguration_CrossSell_ProductType as CrossSellProductType,
  DescriptionPage_DifficultyLevel as DifficultyLevel,
} from '__generated__/graphql-types';

import type { CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import { LEARNING_PRODUCTS, PRODUCT_DIFFICULTY_LEVELS } from 'bundles/search-common/constants';
import CollectionModule from 'bundles/unified-description-page-common/components/modules/collection/CollectionModule';
import {
  useCourseProductCardsQuery,
  useSpecializationProductCardsQuery,
} from 'bundles/unified-description-page-common/hooks/useProductCardQuery';
import type { ProductFromQuery } from 'bundles/unified-description-page-common/hooks/useProductCardQuery';
import type {
  SelfServeConfigCrossSellModuleFragment as SelfServeConfigCrossSellModule,
  SelfServeConfigCrossSellProductFragment as SelfServeConfigCrossSellProduct,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationMarketingPageQuery';

type Props = {
  config: SelfServeConfigCrossSellModule;
};

export const getProductUrl = ({ slug, productType }: { slug: string; productType?: CrossSellProductType | null }) => {
  let basePath = '';

  if (productType === CrossSellProductType.Course) {
    basePath = `/learn/${slug}`;
  } else if (productType === CrossSellProductType.ProfessionalCertificate) {
    basePath = `/professional-certificates/${slug}`;
  } else if (productType === CrossSellProductType.Specialization) {
    basePath = `/specializations/${slug}`;
  }

  return basePath;
};

export const getIdsAndSlugs = (products: SelfServeConfigCrossSellProduct[] = []) => {
  const courseIds: string[] = [];
  const courseSlugs: string[] = [];
  const specializationIds: string[] = [];
  const specializationSlugs: string[] = [];

  products.forEach((product) => {
    if (product.productType === CrossSellProductType.Course) {
      if (product.productSlug) {
        courseSlugs.push(product.productSlug);
      } else if (product.productId) {
        courseIds.push(product.productId);
      }
    } else if (
      product.productType === CrossSellProductType.ProfessionalCertificate ||
      product.productType === CrossSellProductType.Specialization
    ) {
      if (product.productSlug) {
        specializationSlugs.push(product.productSlug);
      } else if (product.productId) {
        specializationIds.push(product.productId);
      }
    }
  });

  return { courseIds, courseSlugs, specializationIds, specializationSlugs };
};

export const mapToProductCardLearningProductType = (productType?: CrossSellProductType | null) => {
  switch (productType) {
    case CrossSellProductType.Specialization:
      return LEARNING_PRODUCTS.Specialization;
    case CrossSellProductType.Course:
      return LEARNING_PRODUCTS.Course;
    case CrossSellProductType.ProfessionalCertificate:
      return LEARNING_PRODUCTS.ProfessionalCertificate;
    default:
      return undefined;
  }
};

export const mapToProductCardDifficultyLevel = (productType?: DifficultyLevel | null) => {
  switch (productType) {
    case DifficultyLevel.Beginner:
      return PRODUCT_DIFFICULTY_LEVELS.Beginner;
    case DifficultyLevel.Intermediate:
      return PRODUCT_DIFFICULTY_LEVELS.Intermediate;
    case DifficultyLevel.Advanced:
      return PRODUCT_DIFFICULTY_LEVELS.Advanced;
    default:
      return undefined;
  }
};

export const getProductsToDisplay = ({
  products,
  productsFromQuery,
}: {
  products: SelfServeConfigCrossSellProduct[];
  productsFromQuery: ProductFromQuery[];
}): CollectionEntity[] => {
  const productsToDisplay: CollectionEntity[] = [];

  const mapToCollectionCardProps = (product: SelfServeConfigCrossSellProduct, productFromQuery: ProductFromQuery) => {
    let productDuration = '';
    if (product.productLengthOverrideText) {
      productDuration = product.productLengthOverrideText || '';
    } else if (productFromQuery.totalLearningDuration) {
      productDuration = productFromQuery.totalLearningDuration || '';
    }

    const slug = product.productSlug ?? productFromQuery.slug;

    return {
      id: product.productId ?? productFromQuery.id,
      name: product.title ?? productFromQuery.name,
      slug,
      imageUrl: product.imageUrl ?? productFromQuery.cardImageUrl,
      link: product.productUrl ?? getProductUrl({ slug, productType: product.productType }),
      productType: mapToProductCardLearningProductType(product.productType),
      partners: productFromQuery.partners?.map((partner) => ({
        id: partner?.id,
        logo: partner?.squareLogo,
        name: partner?.name,
      })),
      productDifficultyLevel: mapToProductCardDifficultyLevel(productFromQuery.difficultyLevel),
      partnerLogos: productFromQuery.partners?.map((partner) => partner?.squareLogo),
      productDuration,
    };
  };

  products.forEach(function (product) {
    const productToDisplay = productsFromQuery.find(
      (productFromQuery) => productFromQuery.slug === product.productSlug || productFromQuery.id === product.productId
    );

    if (productToDisplay) {
      // @ts-expect-error URL: string
      return productsToDisplay.push(mapToCollectionCardProps(product, productToDisplay));
    }
  });

  return productsToDisplay;
};

const CrossSellModuleRenderer = ({ config }: Props) => {
  const { courseIds, courseSlugs, specializationIds, specializationSlugs } = getIdsAndSlugs(config.products);

  const { loading: loadingSpecializationProductCards, productCards: specializationProductCards } =
    useSpecializationProductCardsQuery({ ids: specializationIds, slugs: specializationSlugs });

  const { loading: loadingCourseProductCards, productCards: courseProductCards } = useCourseProductCardsQuery({
    ids: courseIds,
    slugs: courseSlugs,
  });

  const isLoading = loadingSpecializationProductCards || loadingCourseProductCards;

  const productsToDisplay = getProductsToDisplay({
    products: config.products,
    productsFromQuery: [...specializationProductCards, ...courseProductCards],
  });

  if (!config.products?.length) {
    return null;
  }

  return (
    <CollectionModule
      isCollectionLoading={isLoading}
      collection={{
        collectionId: 'cross-sell',
        title: config.title || '',
        entities: productsToDisplay,
      }}
    />
  );
};

export default CrossSellModuleRenderer;
